import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { CheckCircle2, Clock, FileClock, ShieldAlert, ShieldX, XCircle } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { MyApprovalRequest, MyApprovalRequestStatus } from '@/api/approvals-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { formatDateTime } from '@/lib/datetime'

import { ApprovalAmount, BranchCell, ExpiryBadge } from './approval-presentation'
import { ApprovalTrail } from './approval-trail'
import { describeExpiry, describeWait } from './expiry'
import { useCheckerReach, useMyRequests } from './queries'
import { describeRequestType } from './request-types'

/**
 * "What happened to the thing I submitted."
 *
 * The checker's inbox at `/access/approvals/inbox` never contains a maker's own requests — a
 * checker can never be the maker, so the server excludes them outright — which otherwise leaves
 * "sent for approval" with nowhere to go once the dialog closes. This is that somewhere: every
 * request the caller themselves raised, pending or decided in roughly the last month, from
 * `GET /access/approvals/mine`.
 *
 * Read-only throughout. There is no Approve or Refuse here — a maker deciding their own request is
 * exactly what the engine exists to prevent — so this panel only ever says what happened, never
 * offers to make it happen.
 */

const PANEL_ID = 'my-requests-panel'

const STATUS_TONES: Record<MyApprovalRequestStatus, BadgeProps['tone']> = {
  Pending: 'warning',
  Approved: 'brand',
  Rejected: 'danger',
  Expired: 'neutral',
  Executed: 'success',
  Failed: 'danger',
  Unknown: 'neutral',
}

const STATUS_ICONS: Record<MyApprovalRequestStatus, typeof CheckCircle2> = {
  Pending: Clock,
  Approved: CheckCircle2,
  Rejected: ShieldX,
  Expired: FileClock,
  Executed: CheckCircle2,
  Failed: ShieldAlert,
  Unknown: XCircle,
}

export function MyRequestStatusBadge({ status }: { status: MyApprovalRequestStatus }) {
  const Icon = STATUS_ICONS[status]
  return (
    <Badge tone={STATUS_TONES[status]}>
      <Icon className="size-3" aria-hidden />
      {status}
    </Badge>
  )
}

/** Pending first — soonest deadline leading — then decided, most recently decided first. */
function byRelevance(a: MyApprovalRequest, b: MyApprovalRequest): number {
  if (a.status === 'Pending' && b.status !== 'Pending') return -1
  if (b.status === 'Pending' && a.status !== 'Pending') return 1

  if (a.status === 'Pending') {
    return Date.parse(a.expiresAtUtc) - Date.parse(b.expiresAtUtc)
  }

  const aWhen = Date.parse(a.checkedAtUtc ?? a.expiresAtUtc)
  const bWhen = Date.parse(b.checkedAtUtc ?? b.expiresAtUtc)
  return bWhen - aWhen
}

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined
const DEFAULT_PAGE_SIZE = 25

const helper = dataTableColumns<MyApprovalRequest>()

export function MyRequestsPanel() {
  const query = useMyRequests()
  const reach = useCheckerReach()

  const [selected, setSelected] = useState<MyApprovalRequest | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const rows = useMemo(() => [...(query.data ?? [])].sort(byRelevance), [query.data])
  const pendingCount = useMemo(() => rows.filter((row) => row.status === 'Pending').length, [rows])

  const pageRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return rows.slice(start, start + pagination.pageSize)
  }, [rows, pagination])

  const columns = useMemo<DataTableColumn<MyApprovalRequest>[]>(
    () =>
      helper.columns([
        helper.accessor('description', {
          header: 'Request',
          enableSorting: false,
          cell: (context) => (
            <span className="block max-w-md truncate text-sm font-medium text-heading">
              {context.row.original.description}
            </span>
          ),
          meta: { card: 'title' },
        }),
        helper.accessor('requestType', {
          header: 'Operation',
          enableSorting: false,
          cell: (context) => {
            const descriptor = describeRequestType(context.row.original.requestType)
            return (
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-body">{descriptor.label}</span>
                <span className="truncate text-2xs text-muted">
                  {describeWait(context.row.original.madeAtUtc)}
                </span>
              </span>
            )
          },
          meta: { widthClass: 'w-48', card: 'secondary' },
        }),
        helper.accessor('branchId', {
          header: 'Branch',
          enableSorting: false,
          cell: (context) => (
            <BranchCell branchId={context.row.original.branchId} branches={reach.branches} />
          ),
          meta: { noWrap: true, card: 'secondary' },
        }),
        helper.accessor('amountMinor', {
          header: 'Amount',
          enableSorting: false,
          cell: (context) => <ApprovalAmount approval={context.row.original} />,
          meta: { align: 'right', widthClass: 'w-36', card: 'secondary', cardLabel: 'Amount' },
        }),
        helper.accessor('status', {
          header: 'Status',
          enableSorting: false,
          cell: (context) => <MyRequestStatusBadge status={context.row.original.status} />,
          meta: { widthClass: 'w-28', card: 'secondary' },
        }),
        helper.display({
          id: 'when',
          header: 'Deadline / decided',
          cell: (context) => <WhenCell row={context.row.original} />,
          meta: { widthClass: 'w-40', card: 'lead' },
        }),
      ]),
    [reach.branches],
  )

  return (
    <div className="space-y-3">
      <DataTable
        columns={columns}
        data={query.isPending ? undefined : pageRows}
        rowCount={rows.length}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.requestId}
        onRowActivate={setSelected}
        pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
        emptyTitle="You have not submitted anything dual-controlled"
        emptyDescription="Requests you raise that need a second person's countersignature — a new group, a large withdrawal, a role change — appear here the moment you submit them, whether they are still waiting or have already been decided."
      />

      {rows.length > 0 ? (
        <p className="text-2xs text-muted">
          {pendingCount > 0
            ? `${String(pendingCount)} still waiting on a countersignature. `
            : null}
          Decided requests stay listed for about thirty days; a pending one stays until it is
          decided or lapses. Re-read every minute, same as the queue you submit into.
        </p>
      ) : null}

      {selected ? (
        <MyRequestDetail
          key={selected.requestId}
          request={selected}
          onOpenChange={(open) => {
            if (!open) {
              setSelected(null)
            }
          }}
        />
      ) : null}
    </div>
  )
}

function WhenCell({ row }: { row: MyApprovalRequest }) {
  if (row.status === 'Pending') {
    return <ExpiryBadge expiresAtUtc={row.expiresAtUtc} />
  }

  const at = row.checkedAtUtc ?? row.expiresAtUtc
  return (
    <span className="flex min-w-0 flex-col text-right">
      <span className="truncate text-xs text-body">{formatDateTime(at)}</span>
      {row.checkerUsername ? (
        <span className="truncate text-2xs text-muted">by {row.checkerUsername}</span>
      ) : null}
    </span>
  )
}

type DetailTab = 'request' | 'history'

function MyRequestDetail({
  request,
  onOpenChange,
}: {
  request: MyApprovalRequest
  onOpenChange: (open: boolean) => void
}) {
  const reach = useCheckerReach()
  const [tab, setTab] = useState<DetailTab>('request')
  const descriptor = describeRequestType(request.requestType)
  const expiry = describeExpiry(request.expiresAtUtc)

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          onOpenChange(false)
        }
      }}
    >
      <SheetContent aria-describedby={undefined} className="max-w-2xl">
        <SheetHeader>
          <SheetTitle>{descriptor.label}</SheetTitle>
          <SheetDescription>
            <span className="font-mono">{descriptor.code}</span> · request{' '}
            <span className="font-mono">{request.requestId}</span>
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-4">
          <div className="flex items-center justify-between">
            <MyRequestStatusBadge status={request.status} />
            {request.status === 'Pending' ? (
              <span className="text-xs text-muted">
                {expiry.urgency === 'expired' ? 'Lapses imminently' : expiry.label}
              </span>
            ) : null}
          </div>

          <TabStrip<DetailTab>
            tabs={
              [
                { id: 'request', label: 'Summary' },
                { id: 'history', label: 'History' },
              ] satisfies TabDefinition<DetailTab>[]
            }
            active={tab}
            onChange={setTab}
            panelId={PANEL_ID}
            label="Submission sections"
          />

          <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
            {tab === 'request' ? (
              <div className="space-y-4">
                <div className="rounded-card border border-border-subtle bg-surface-muted p-3">
                  <p className="text-sm text-heading">{request.description}</p>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field
                      label="Submitted"
                      value={`${formatDateTime(request.madeAtUtc)} · ${describeWait(request.madeAtUtc)}`}
                    />
                    <Field
                      label="Branch"
                      value={<BranchCell branchId={request.branchId} branches={reach.branches} />}
                    />
                    <Field label="Amount" value={<ApprovalAmount approval={request} />} />
                    <Field
                      label={request.status === 'Pending' ? 'Lapses' : 'Deadline was'}
                      value={<ExpiryBadge expiresAtUtc={request.expiresAtUtc} />}
                    />
                  </dl>
                </div>

                {request.status === 'Pending' ? (
                  <p className="text-xs text-muted">
                    Nothing has changed yet. This takes effect the moment someone holding{' '}
                    <span className="font-mono text-2xs">{descriptor.checkerPermission}</span>{' '}
                    countersigns it — or it lapses at the deadline above and has to be resubmitted.
                  </p>
                ) : request.checkerUsername ? (
                  <div className="rounded-card border border-border-subtle bg-surface p-3 text-sm">
                    <p className="text-heading">
                      {request.status === 'Rejected' ? 'Refused' : 'Decided'} by{' '}
                      <span className="font-medium">{request.checkerUsername}</span>
                      {request.checkedAtUtc ? ` · ${formatDateTime(request.checkedAtUtc)}` : null}
                    </p>
                    {request.checkerNote ? (
                      <p className="mt-1.5 text-xs text-body">
                        {request.status === 'Rejected' ? 'Reason: ' : 'Note: '}
                        &ldquo;{request.checkerNote}&rdquo;
                      </p>
                    ) : null}
                    {request.status === 'Failed' && request.failureCode ? (
                      <p className="mt-1.5 text-xs text-danger-text">
                        Approved, but re-running it failed re-validation:{' '}
                        <span className="font-mono text-2xs">{request.failureCode}</span>. Nothing
                        changed — the amount, balance, or period it depended on had moved by the
                        time it ran.
                      </p>
                    ) : null}
                    {request.status === 'Executed' ? (
                      <p className="mt-1.5 text-xs text-success-text">
                        Approved and completed{' '}
                        {request.executedAtUtc ? `at ${formatDateTime(request.executedAtUtc)}` : ''}.
                      </p>
                    ) : null}
                  </div>
                ) : request.status === 'Expired' ? (
                  <p className="text-xs text-muted">
                    Lapsed without a decision at {formatDateTime(request.expiresAtUtc)}. Nobody with
                    the countersigning permission acted in time — nothing changed, and it has to be
                    submitted again.
                  </p>
                ) : null}
              </div>
            ) : (
              <ApprovalTrail requestId={request.requestId} enabled={tab === 'history'} />
            )}
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-heading">{value}</dd>
    </div>
  )
}
